import {
  ChooseType,
  Facility,
  HgSupportedLanguage,
  NumberRange,
  ObjectType,
  OfferType,
  SearchModel,
  SearchResult,
} from '@smg-real-estate/hg-search-libs';
import { type Composer } from 'vue-i18n';

import { localizeLanguage } from '@/helpers/LocalizeLanguage/LocalizeLanguage';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { AppStore } from '@/store/types';
import { AxiosError } from 'axios';

const KEY_PREFIX = 'resultsList.heading';
const getGeoLocationName = 'geo/getGeoLocationName';
const withPlatformName = (title: string) =>
  `${title} | ${isPlatformIs24() ? 'ImmoScout24' : 'homegate.ch'}`;

type ObjectTypeKey = keyof typeof ObjectType;

/* eslint-disable-next-line max-params */
export function generateCategoryString(
  i18n: Composer,
  chooseType: ChooseType | undefined,
  offerType: string | undefined,
  objectTypes: string[],
  facilities: Facility[] | undefined,
  roomsRange: NumberRange | undefined,
  keyPrefix = KEY_PREFIX,
): string {
  const { t } = i18n;
  const objectTypeKeys = Object.keys(ObjectType) as ObjectTypeKey[];

  if (objectTypes !== undefined && objectTypes.length >= 1) {
    const categoryStrings = objectTypes
      .map((x) => {
        const objectType = objectTypeKeys.find((key) => ObjectType[key] === x);
        return objectType && t(`listingCategories.${objectType}`);
      })
      .filter((x) => x);

    if (categoryStrings.length > 0) {
      const offerTypeString =
        offerType && ' '.concat(t(`resultlist.offerType.${offerType}`).toString());

      return [categoryStrings.join(', '), offerTypeString].join('');
    }
  }

  if (facilities !== undefined && facilities.length >= 1) {
    const categoryStrings = facilities
      .map((facility) => facility && t(`searchBar.${facility}`))
      .filter((x) => x);

    if (categoryStrings.length > 0) {
      const offerTypeString =
        offerType && ' '.concat(t(`resultlist.offerType.${offerType}`).toString());

      return [categoryStrings.join(', '), offerTypeString].join('');
    }
  }

  if (
    !isPlatformIs24() &&
    !!roomsRange &&
    isNumberOfRoomsValidForCanonicalUrl(roomsRange, chooseType)
  ) {
    const chooseTypeString = chooseType && t(`resultsList.heading.${chooseType}`);
    const roomsString = t(`metaInfo.srp.rooms.${roomsRange.from}RoomApartment`);

    return [chooseTypeString, roomsString].join(' ');
  }

  return t(`${keyPrefix}.${chooseType}`);
}

export function generateSearchHeading(
  { chooseType, objectTypes, offerType, locations, facilitiesRequired, numberOfRooms }: SearchModel,
  i18n: Composer,
  store: AppStore,
  resultCount?: number | undefined,
): string {
  const { t } = i18n;
  let titleKey: string;

  if (locations.length && resultCount) {
    titleKey = 'title';
  } else if (locations.length) {
    titleKey = 'noResultTitle';
  } else {
    titleKey = 'titleWithoutLocation';
  }

  const titleTemplateAccordingToLocations = `resultsList.${titleKey}`;
  const isNewBuilding = isNewBuildingFacility(chooseType, facilitiesRequired);

  let categoryString: string;
  if (isNewBuilding) {
    const newBuildingKey =
      offerType === OfferType.RENT
        ? `${KEY_PREFIX}.rentnewbuilding`
        : `${KEY_PREFIX}.purchnewbuilding`;
    categoryString = t(newBuildingKey);
  } else {
    categoryString = generateCategoryString(
      i18n,
      chooseType,
      offerType,
      objectTypes,
      undefined,
      numberOfRooms,
    );
  }

  const [categoryPart1, categoryPart2] = splitCategoryString(
    categoryString.toString(),
    i18n,
    isNewBuilding,
    chooseType,
  );

  const loc = locations
    .map((locationId) => store.getters[getGeoLocationName](locationId, i18n.locale.value))
    .join(', ');

  return t(titleTemplateAccordingToLocations, {
    category: categoryString,
    categoryPart1: categoryPart1,
    loc,
    categoryPart2: categoryPart2,
  }) as string;
}

function isNumberOfRoomsValidForCanonicalUrl(
  numberOfRooms: NumberRange | undefined,
  chooseType: ChooseType | undefined,
) {
  if (!numberOfRooms || !isValidRoomChooseType(chooseType)) {
    return false;
  }

  const { from, to } = numberOfRooms;

  // If from is undefined, we don't want to use it for canonical URL
  if (!from) {
    return false;
  }

  const MAX_CANONICAL_FROM_ROOMS = 6;

  let isFromRoomsEligible = from < MAX_CANONICAL_FROM_ROOMS;

  if (!isPlatformIs24()) {
    isFromRoomsEligible &&= Number.isInteger(from);
  }

  const hasMinimalRoomsStep = to && to - from === 0.5;

  const hasFiveOrMoreRooms = !to && from === 5;

  return isFromRoomsEligible && (hasMinimalRoomsStep || hasFiveOrMoreRooms);
}

function isValidRoomChooseType(chooseType: ChooseType | undefined) {
  return (
    chooseType &&
    [
      ChooseType.rentflatonly,
      ChooseType.rentflat,
      ChooseType.purchflat,
      ChooseType.purchfaofh,
    ].includes(chooseType)
  );
}

export function splitCategoryString(
  categoryString: string,
  i18n: Composer,
  isNewBuilding: boolean,
  chooseType?: ChooseType,
): [string, string] {
  let categoryPart1: string, categoryPart2: string;
  const { locale } = i18n;

  if (
    !isNewBuilding &&
    chooseType &&
    locale.value === HgSupportedLanguage.DE &&
    [ChooseType.purchfaofh].includes(chooseType)
  ) {
    [categoryPart1, categoryPart2] = categoryString.split(' ');
    categoryPart2 = ' ' + categoryPart2;
  } else {
    categoryPart1 = categoryString;
    categoryPart2 = '';
  }

  return [categoryPart1, categoryPart2];
}
function isRealEstateChooseType(chooseType: ChooseType): boolean {
  return [ChooseType.rentflat, ChooseType.purchfaofh].includes(chooseType);
}

function isNewBuildingFacility(
  chooseType: ChooseType | undefined,
  facilitiesRequired: Facility[],
): boolean {
  return (
    !!chooseType &&
    isRealEstateChooseType(chooseType) &&
    facilitiesRequired.length === 1 &&
    facilitiesRequired[0] === Facility.NEWLY_BUILT
  );
}

function getRoomsRangeForPageTitle(
  i18n: Composer,
  numberOfRooms: NumberRange | undefined,
  chooseType: ChooseType | undefined,
): string {
  const { t } = i18n;

  const isValidRoomRange =
    isPlatformIs24() && isNumberOfRoomsValidForCanonicalUrl(numberOfRooms, chooseType);
  if (!isValidRoomRange) {
    return '';
  }

  const roomPlural = t('resultListSeoLinkList.roomPlural');
  return ` ${numberOfRooms?.from} - ${numberOfRooms?.to} ${roomPlural}`;
}

export function generatePageTitleWithoutDomainSuffix(
  {
    chooseType,
    objectTypes,
    offerType,
    locations,
    page,
    numberOfRooms,
    facilitiesRequired,
  }: SearchModel,
  i18n: Composer,
  store: AppStore,
  resultCount?: number | undefined,
): string {
  const { t } = i18n;

  const titleAccordingToLocations = `${locations.length > 0 ? 'title' : 'titleWithoutLocation'}`;
  const titleAccordingToPage = `${page > 1 ? 'titleWithPage' : titleAccordingToLocations}`;
  const titleTemplate = `metaInfo.srp.${titleAccordingToPage}`;
  const isNewBuilding = isNewBuildingFacility(chooseType, facilitiesRequired);

  let categoryString: string;
  if (isNewBuilding) {
    const newBuildingKey =
      offerType === OfferType.RENT ? 'titleForRentNewBuilding' : 'titleForPurchNewBuilding';
    categoryString = t(`metaInfo.srp.${newBuildingKey}`);
  } else {
    categoryString = generateCategoryString(
      i18n,
      chooseType,
      offerType,
      objectTypes,
      undefined,
      numberOfRooms,
    );
  }

  const roomsRange = getRoomsRangeForPageTitle(i18n, numberOfRooms, chooseType);
  const [categoryPart1, categoryPart2] = splitCategoryString(
    categoryString.toString(),
    i18n,
    isNewBuilding,
    chooseType,
  );

  const numberOfObjects = page === 1 && resultCount && resultCount > 4 ? `${resultCount} ` : '';
  const loc = locations
    .map((locationId) => store.getters[getGeoLocationName](locationId, i18n.locale.value))
    .join(', ');

  return t(titleTemplate, {
    numberOfObjects,
    category: categoryString,
    categoryPart1: categoryPart1,
    loc,
    categoryPart2: categoryPart2,
    page: page,
    roomsRange,
  }) as string;
}

export const generatePageTitle = (
  searchModel: SearchModel,
  i18n: Composer,
  store: AppStore,
  resultCount?: number | undefined,
): string =>
  withPlatformName(generatePageTitleWithoutDomainSuffix(searchModel, i18n, store, resultCount));

const quickFilterMapper = {
  [Facility.ANIMALS_ALLOWED]: 'pets',
  [Facility.HAS_BALCONY]: 'balcony',
  [Facility.HAS_ELEVATOR]: 'elevator',
};

export function generatePageDescription(
  searchModel: SearchModel,
  result: SearchResult | null | undefined,
  dataFetchError: AxiosError | Error | null,
  i18n: Composer,
  store: AppStore,
): string {
  if (dataFetchError) {
    return withPlatformName('An error occurred');
  }

  // eslint-disable-next-line prettier/prettier
    const {
      chooseType,
      objectTypes,
      offerType,
      locations,
      page,
      numberOfRooms,
      facilitiesRequired,
    } = searchModel;

  const { t } = i18n;

  const categoryStringKeyPrefix = isPlatformIs24() ? 'resultsList.altHeading' : KEY_PREFIX;
  const categoryString = generateCategoryString(
    i18n,
    chooseType,
    offerType,
    objectTypes,
    undefined,
    numberOfRooms,
    categoryStringKeyPrefix,
  );

  const descriptionTemplate = getDescriptionTemplate(
    page,
    facilitiesRequired,
    offerType,
    chooseType,
    numberOfRooms,
  );

  const loc = locations
    .map((locationId) => store.getters[getGeoLocationName](locationId, i18n.locale.value))
    .join(', ');
  const numberOfHits = result ? result.resultCount : '';

  return t(descriptionTemplate, {
    category: categoryString,
    page: page,
    loc,
    numberOfHits,
  }) as string;
}

export function generateAlternateLinks(
  alternatePageLink: (language: HgSupportedLanguage) => string,
): { rel: string; href: string; hreflang: string }[] {
  const links = [
    HgSupportedLanguage.DE,
    HgSupportedLanguage.IT,
    HgSupportedLanguage.EN,
    HgSupportedLanguage.FR,
  ].map((language) => {
    return {
      rel: 'alternate',
      hreflang: localizeLanguage(language),
      href: `${import.meta.env.VUE_PLATFORM_BASE_URL}${alternatePageLink(language)}`,
    };
  });

  return [...links, addXDefaultAlternateLink(alternatePageLink)];
}

export function addXDefaultAlternateLink(
  alternatePageLink: (language: HgSupportedLanguage) => string,
): {
  rel: string;
  href: string;
  hreflang: string;
} {
  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${import.meta.env.VUE_PLATFORM_BASE_URL}${alternatePageLink(HgSupportedLanguage.DE)}`,
  };
}

export function getDescriptionTemplate(
  page: number,
  facilitiesRequired: Facility[],
  offerType?: OfferType,
  chooseType?: ChooseType,
  numberOfRooms?: NumberRange,
): string {
  if (isNewBuildingFacility(chooseType, facilitiesRequired)) {
    const newBuildingSuffix = offerType === OfferType.RENT ? 'Rent' : 'Purch';
    return `metaInfo.srp.descriptionFor${newBuildingSuffix}NewBuilding`;
  }

  if (
    !isPlatformIs24() &&
    numberOfRooms &&
    isNumberOfRoomsValidForCanonicalUrl(numberOfRooms, chooseType)
  ) {
    return `metaInfo.srp.rooms.description.template`;
  }

  const pageTemplate = page > 1 ? 'descriptionWithPage' : 'description';
  return `metaInfo.srp.${pageTemplate}`;
}
